<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'contractAttachments',
    path: '/contract-attachments',
    component: 'ContractAttachments',
    title: () => pgettext('ContractAttachments|Title', 'Contract Attachments'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractAttachments',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const gridName = ref('contractAttachments');
  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'contract_revision',
      name: $fn.value.pgettext('ContractAttachments', 'Contract'),
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    {{ $value.contract_number }}\n\
    {{~#if $value.revision_number }}\n\
      /{{ $value.revision_number }}\n\
    {{~/if }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            params: {
              id: 'contract_revision.contract_id',
            },
          },
        ],
      },
    },
    {
      field: 'file_name',
      name: $fn.value.pgettext('ContractAttachments', 'File name'),
      type: 'command',
      formatter: {
        name: 'attachment',
        useDataContext: true,
        displayFileName: true,
      },
    },
    {
      field: 'file_size',
      name: $fn.value.pgettext('ContractAttachments', 'File size'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'description',
      name: $fn.value.pgettext('ContractAttachments', 'Description'),
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('BrowserColumn', 'Create time'),
      formatter: 'datetime',
    },
    {
      field: 'created_by',
      name: $fn.value.pgettext('ContractAttachments', 'Created by'),
      formatter: {
        name: 'user',
      },
    },
  ]);

  const $data = computed(() => ({
    gridName: gridName.value,
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('browse_contract_attachments');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'gridName' },
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      gridName,
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'ContractAttachments|Title',
        'Contract Attachments'
      ),
      icon: $config.value.attachment.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_p7a3qlecx_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_3ps8m8c22_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_script_9c4t8nv0a_value_callable = async (
    $event
  ) => {
    return (
      console.log($event),
      $app.value.attachments.downloadAttachment($event.data.data)
    );
  };
  const act_2248226175642056_script_9c4t8nv0a_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_9c4t8nv0a_value_callable($event);
      },
    };
  };
  const inst_evh_2248226175642056_command_v6tq7ic98_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'file_name';
  };

  const act_7315092382398562_closeModal_p7a3qlecx =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_p7a3qlecx_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ss126i91e =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_p7a3qlecx,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ss126i91e],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_5c5v5xjqc =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_sbq7iau50 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_5c5v5xjqc,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_sbq7iau50],
    event: 'reload',
  });
  const act_2248226175642056_setData_3ps8m8c22 = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_3ps8m8c22_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_fzs1cs3se =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_3ps8m8c22,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_fzs1cs3se],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_script_9c4t8nv0a = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_9c4t8nv0a_getActionArgs,
  });
  const inst_evh_2248226175642056_command_v6tq7ic98 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_9c4t8nv0a,
      event: 'command',
      when: inst_evh_2248226175642056_command_v6tq7ic98_getWhen,
      displayName: 'script',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_command_v6tq7ic98],
    event: 'command',
  });

  const actions = {
    inst_evh_7315092382398562_close_ss126i91e,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_sbq7iau50,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_fzs1cs3se,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_v6tq7ic98,
    inst_evh_2248226175642056_command,
  };

  const evh_7315092382398562_close_ss126i91e = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ss126i91e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_sbq7iau50 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_sbq7iau50.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_fzs1cs3se = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_fzs1cs3se.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_v6tq7ic98 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_v6tq7ic98.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractAttachments"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      :name="gridName"
      :headerTitle="documentTitle"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'contract-attachments')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <PageHelp path="/products/products.html"> </PageHelp>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
